/**
 * AdminDanismanIstatistik.jsx
 *
 * Danışman İstatistikleri — filtre barı + 3 metrik kutusu + 6 sekme.
 * Route: /danisman/istatistik
 * Erişim: danisman, admin, superadmin
 *
 * Backend: GET routes/danisman_istatistik.py::danisman_istatistik
 *   GET /danisman/istatistik?danisman_id=&aktif=&ders=&bas_tarih=&bit_tarih=&tarih_tipi=
 *   Yanıt: { filtreler, metrikler, sekmeler } — TEK istekte hepsi gelir, sekme
 *   değişiminde yeniden fetch YAPILMAZ (bkz. modülün dosya başı yorumu).
 *
 * Rol izolasyonu (bkz. backend dosya başı yorumu): danisman rolünde
 * `danisman_id` ve `aktif` sunucuda HER ZAMAN yok sayılır — bu yüzden bu iki
 * filtre burada da yalnızca admin/superadmin'e render edilir (aşağıdaki
 * `isAdmin` guard'ları). Danışman rolünde ekranda "tüm ekip" ima eden bir
 * başlık/metin YOKTUR — kendi verisine baktığı açıkça belirtilir (bkz.
 * `subtitle` ve Atanan Datalar sekmesindeki rol bazlı metin).
 *
 * Sıfıra bölme: backend oran/ortalama alanlarında payda 0 ise `null` döner
 * (0 DEĞİL — "oran sıfır" ile "veri yok" ayrımı içindir). Bu ekran `null`
 * için her yerde "—" gösterir, asla "%0" basmaz (bkz. `RatioBar`, metrik
 * kutuları).
 *
 * Eğitim/ders filtresi sınırı (bkz. backend dosya başı yorumu): `ders`
 * parametresi yalnızca leads tabanlı sekmelerde/metriklerde uygulanır —
 * payments tabanlı sekmelerde (Öğrenci Satış, Tahsilat) ve ilgili metrik
 * kutularında (Satış Adet/Data Adet, Tahsilat/Satış) HİÇ uygulanmaz (backend
 * payments sorgusunda `ders` alanı yok). Bu ekran `ders` filtresi doluyken
 * o iki sekmede + metrik kutuları altında kısa bir dipnot gösterir.
 */
import { useState, useCallback, useEffect, useId } from 'react';
import apiClient from '../services/apiClient';
import AdminLayout from '../components/admin/AdminLayout';
import PageHeader from '../components/admin/PageHeader';
import ErrorBoundary, { BlockErrorFallback } from '../components/ErrorBoundary';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { getApiErrorMessage } from '../utils/apiError';
import { fmtMoney, fmtPercent } from '../utils/money';
import { rootBreadcrumb } from '../utils/adminBreadcrumbs';
import { Skeleton } from '../components/ui/skeleton';
import {
  useLeadStatusOptions, leadStatusColors, leadStatusLabel,
} from '../hooks/useLeadStatusOptions';
import { useDanismanlarOptions, danismanOptionsList } from '../hooks/useDanismanlarOptions';
import { KpiCard, KpiSkeleton } from '../components/crm/CrmKpiPrimitives';
import { ErrorRetryCrm, INPUT_CLS, LBL_CLS } from '../components/crm/DanismanCockpitShared';
import {
  Search, TrendingUp, Wallet, PhoneCall, Info,
} from 'lucide-react';

const AKTIF_OPTIONS = [
  { value: 'tumu', tr: 'Tümü', en: 'All' },
  { value: 'true', tr: 'Aktif', en: 'Active' },
  { value: 'false', tr: 'Pasif', en: 'Inactive' },
];

const TARIH_TIPI_OPTIONS = [
  { value: 'data_tarihi', tr: 'Data Tarihi', en: 'Data Date' },
  { value: 'son_islem', tr: 'Son İşlem', en: 'Last Action' },
];

const EMPTY_FILTERS = {
  danismanId: '',
  aktif: 'tumu',
  ders: '',
  basTarih: '',
  bitTarih: '',
  tarihTipi: 'data_tarihi',
};

const TAB_DEFS = [
  { id: 'durum_dagilimi', tr: 'Durum Dağılımı', en: 'Status Distribution' },
  { id: 'atanan_datalar', tr: 'Atanan Datalar', en: 'Assigned Leads' },
  { id: 'arama_dagilimi', tr: 'Arama Dağılımı', en: 'Call Distribution' },
  { id: 'ogrenci_satis', tr: 'Öğrenci Satış', en: 'Student Sales' },
  { id: 'tahsilat', tr: 'Tahsilat', en: 'Collections' },
  { id: 'datanin_satis_durumu', tr: 'Datanın Satış Durumu', en: 'Lead Conversion Status' },
];

const ARAMA_BUCKET_LABELS = [
  { key: '0', tr: '0 Arama', en: '0 Calls' },
  { key: '1', tr: '1 Arama', en: '1 Call' },
  { key: '2_3', tr: '2-3 Arama', en: '2-3 Calls' },
  { key: '4_plus', tr: '4+ Arama', en: '4+ Calls' },
];

function buildParams(f, isAdmin) {
  const p = new URLSearchParams();
  if (isAdmin && f.danismanId) p.set('danisman_id', f.danismanId);
  if (isAdmin && f.aktif && f.aktif !== 'tumu') p.set('aktif', f.aktif);
  if (f.ders) p.set('ders', f.ders);
  if (f.basTarih) p.set('bas_tarih', f.basTarih);
  if (f.bitTarih) p.set('bit_tarih', f.bitTarih);
  p.set('tarih_tipi', f.tarihTipi || 'data_tarihi');
  return p;
}

/** Oran çubuğu — renk/uzunluk TEK bilgi taşıyıcısı değil, yüzde her zaman
 * metin olarak da görünür (WCAG — bkz. görev talimatı erişilebilirlik notu). */
function RatioBar({ pct, lang }) {
  const width = pct != null ? Math.min(100, Math.max(0, pct)) : 0;
  return (
    <div className="flex items-center gap-2 min-w-[130px]">
      <div className="flex-1 h-2 rounded-full bg-gray-100 overflow-hidden" aria-hidden="true">
        <div className="h-full rounded-full" style={{ width: `${width}%`, backgroundColor: 'var(--crm-navy)' }} />
      </div>
      <span className="text-xs font-bold flex-shrink-0 text-right" style={{ color: 'var(--crm-muted)', minWidth: 56 }}>
        {pct != null ? fmtPercent(pct, lang) : '—'}
      </span>
    </div>
  );
}

function DersFiltreDipnotu({ tr }) {
  return (
    <p
      className="flex items-start gap-2 text-xs font-semibold p-2.5 rounded-lg mb-3"
      style={{ backgroundColor: 'rgba(46,107,143,0.08)', border: '1px solid rgba(46,107,143,0.25)', color: 'var(--crm-info)' }}
    >
      <Info size={13} className="flex-shrink-0 mt-0.5" aria-hidden="true" />
      <span>
        {tr(
          'Eğitim/ders filtresi bu bölümdeki tutar ve adetlere uygulanmaz — burada gösterilen satış/tahsilat verisi seçili eğitimden bağımsız, dönemin tamamını kapsar.',
          'The course filter does not apply to the figures in this section — the sales/collection data shown here covers the whole period, independent of the selected course.'
        )}
      </span>
    </p>
  );
}

function fmtRatio(n, lang) {
  if (n == null) return '—';
  return Number(n).toLocaleString(lang === 'tr' ? 'tr-TR' : 'en-US', { maximumFractionDigits: 2 });
}

// ─── Sekme içerikleri ────────────────────────────────────────────────────────

function DurumDagilimiTab({ rows, statusOptions, lang, tr }) {
  const list = rows || [];
  const total = list.reduce((acc, r) => acc + (r.adet || 0), 0);
  if (list.length === 0) {
    return <p className="p-4 text-sm" style={{ color: 'var(--crm-muted)' }}>{tr('Kayıt yok.', 'No records.')}</p>;
  }
  return (
    <div className="max-h-[440px] overflow-y-auto">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 sticky top-0">
          <tr>
            <th scope="col" className="text-left p-3 font-bold">{tr('Durum', 'Status')}</th>
            <th scope="col" className="text-right p-3 font-bold">{tr('Adet', 'Count')}</th>
            <th scope="col" className="text-left p-3 font-bold">{tr('Oran', 'Ratio')}</th>
          </tr>
        </thead>
        <tbody>
          {list.map((row) => {
            const { bg, text, border } = leadStatusColors(row.status);
            const label = leadStatusLabel(row.status, statusOptions) || row.label || row.status;
            const pct = total ? (row.adet / total) * 100 : null;
            return (
              <tr key={row.status} className="border-t" style={{ borderColor: 'var(--crm-card-border)' }}>
                <td className="p-3">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold border ${bg} ${text} ${border}`}>
                    {label}
                  </span>
                </td>
                <td className="p-3 text-right font-bold" style={{ color: 'var(--crm-navy-text)' }}>{row.adet}</td>
                <td className="p-3"><RatioBar pct={pct} lang={lang} /></td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function AtananDatalarTab({ block, isDanisman, isSingleScope, lang, tr }) {
  const toplam = block?.toplam ?? 0;
  const kirilim = block?.danisman_kirilimi;

  const aciklama = isDanisman
    ? tr('Size atanmış toplam data sayısı.', 'Total leads assigned to you.')
    : tr('Seçili kapsamda atanmış toplam data sayısı.', 'Total leads assigned within the selected scope.');

  if (isSingleScope || !Array.isArray(kirilim)) {
    return (
      <div className="p-4">
        <KpiCard label={tr('Toplam Atanan Data', 'Total Assigned Leads')} value={toplam} sub={aciklama} />
      </div>
    );
  }

  if (kirilim.length === 0) {
    return <p className="p-4 text-sm" style={{ color: 'var(--crm-muted)' }}>{tr('Kayıt yok.', 'No records.')}</p>;
  }

  return (
    <div className="max-h-[440px] overflow-y-auto">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 sticky top-0">
          <tr>
            <th scope="col" className="text-left p-3 font-bold">{tr('Danışman', 'Advisor')}</th>
            <th scope="col" className="text-right p-3 font-bold">{tr('Adet', 'Count')}</th>
            <th scope="col" className="text-left p-3 font-bold">{tr('Oran', 'Ratio')}</th>
          </tr>
        </thead>
        <tbody>
          {kirilim.map((row) => {
            const pct = toplam ? (row.adet / toplam) * 100 : null;
            return (
              <tr key={row.danisman_id} className="border-t" style={{ borderColor: 'var(--crm-card-border)' }}>
                <td className="p-3 font-semibold" style={{ color: 'var(--crm-navy-text)' }}>
                  {row.danisman_adi || row.danisman_id}
                </td>
                <td className="p-3 text-right font-bold" style={{ color: 'var(--crm-navy-text)' }}>{row.adet}</td>
                <td className="p-3"><RatioBar pct={pct} lang={lang} /></td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function AramaDagilimiTab({ block, lang, tr }) {
  const b = block || {};
  const total = ARAMA_BUCKET_LABELS.reduce((acc, k) => acc + (b[k.key] || 0), 0);
  return (
    <div className="max-h-[440px] overflow-y-auto">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 sticky top-0">
          <tr>
            <th scope="col" className="text-left p-3 font-bold">{tr('Arama Sayısı', 'Call Count')}</th>
            <th scope="col" className="text-right p-3 font-bold">{tr('Data Adedi', 'Lead Count')}</th>
            <th scope="col" className="text-left p-3 font-bold">{tr('Oran', 'Ratio')}</th>
          </tr>
        </thead>
        <tbody>
          {ARAMA_BUCKET_LABELS.map((bucket) => {
            const adet = b[bucket.key] || 0;
            const pct = total ? (adet / total) * 100 : null;
            return (
              <tr key={bucket.key} className="border-t" style={{ borderColor: 'var(--crm-card-border)' }}>
                <td className="p-3 font-semibold" style={{ color: 'var(--crm-navy-text)' }}>
                  {lang === 'tr' ? bucket.tr : bucket.en}
                </td>
                <td className="p-3 text-right font-bold" style={{ color: 'var(--crm-navy-text)' }}>{adet}</td>
                <td className="p-3"><RatioBar pct={pct} lang={lang} /></td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function OgrenciSatisTab({ block, dersActive, tr }) {
  const b = block || {};
  return (
    <div className="p-4">
      {dersActive && <DersFiltreDipnotu tr={tr} />}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <KpiCard label={tr('Dönüşen Öğrenci Adedi', 'Converted Students')} value={b.donusen_ogrenci_adedi ?? 0} />
        <KpiCard label={tr('Satış Adedi', 'Sales Count')} value={b.satis_adedi ?? 0} />
        <KpiCard label={tr('Satış Tutarı', 'Sales Amount')} value={fmtMoney(b.satis_tutari)} />
      </div>
    </div>
  );
}

function TahsilatTab({ block, dersActive, tr }) {
  const b = block || {};
  return (
    <div className="p-4">
      {dersActive && <DersFiltreDipnotu tr={tr} />}
      <p className="flex items-start gap-2 text-xs font-semibold p-2.5 rounded-lg mb-3" style={{ backgroundColor: 'var(--crm-cream)', color: 'var(--crm-muted)' }}>
        <Info size={13} className="flex-shrink-0 mt-0.5" aria-hidden="true" />
        <span>
          {tr(
            '"Kalan Tutar" ve "Kalan Borç Adedi" seçili tarih aralığından bağımsızdır — o anki ödenmemiş güncel bakiyeyi gösterir.',
            '"Remaining Amount" and "Remaining Debt Count" are independent of the selected date range — they show the current unpaid balance.'
          )}
        </span>
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <KpiCard label={tr('Tahsil Edilen', 'Collected')} value={fmtMoney(b.tahsil_edilen)} />
        <KpiCard label={tr('Kalan Tutar', 'Remaining Amount')} value={fmtMoney(b.kalan_tutar)} />
        <KpiCard label={tr('Kalan Borç Adedi', 'Remaining Debt Count')} value={b.kalan_adet ?? 0} />
      </div>
    </div>
  );
}

function DataninSatisDurumuTab({ block, lang, tr }) {
  const b = block || {};
  const toplam = b.toplam ?? 0;
  const rows = [
    { key: 'donusmus', label: tr('Dönüşmüş', 'Converted'), adet: b.donusmus ?? 0 },
    { key: 'donusmemis', label: tr('Dönüşmemiş', 'Not Converted'), adet: b.donusmemis ?? 0 },
  ];
  return (
    <div className="max-h-[440px] overflow-y-auto">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 sticky top-0">
          <tr>
            <th scope="col" className="text-left p-3 font-bold">{tr('Durum', 'Status')}</th>
            <th scope="col" className="text-right p-3 font-bold">{tr('Adet', 'Count')}</th>
            <th scope="col" className="text-left p-3 font-bold">{tr('Oran', 'Ratio')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const pct = toplam ? (row.adet / toplam) * 100 : null;
            return (
              <tr key={row.key} className="border-t" style={{ borderColor: 'var(--crm-card-border)' }}>
                <td className="p-3 font-semibold" style={{ color: 'var(--crm-navy-text)' }}>{row.label}</td>
                <td className="p-3 text-right font-bold" style={{ color: 'var(--crm-navy-text)' }}>{row.adet}</td>
                <td className="p-3"><RatioBar pct={pct} lang={lang} /></td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ─── Ana içerik (filtre + metrik + sekme — tek fetch) ───────────────────────

function IstatistikContent({ lang, tr, authHeaders, isAdmin, isDanisman }) {
  const toast = useToast();
  const { options: statusOptions } = useLeadStatusOptions();
  const { danismanlar } = useDanismanlarOptions(authHeaders, { includeAdmins: false, enabled: isAdmin });

  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const setField = (key, value) => setFilters((prev) => ({ ...prev, [key]: value }));

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [activeTab, setActiveTab] = useState(TAB_DEFS[0].id);

  const tablistId = useId();

  const fetchData = useCallback(async (f) => {
    setLoading(true);
    setError(false);
    try {
      const params = buildParams(f, isAdmin);
      const r = await apiClient.get('/danisman/istatistik', { params });
      setData(r.data);
    } catch (err) {
      setError(true);
      toast.error(getApiErrorMessage(err, lang));
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, lang]);

  // İlk yükleme — bir kez.
  useEffect(() => {
    fetchData(EMPTY_FILTERS);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyFilters = () => fetchData(filters);
  const clearFilters = () => {
    setFilters(EMPTY_FILTERS);
    fetchData(EMPTY_FILTERS);
  };

  const activeFilterCount = [
    isAdmin && filters.danismanId ? filters.danismanId : null,
    isAdmin && filters.aktif !== 'tumu' ? filters.aktif : null,
    filters.ders,
    filters.basTarih,
    filters.bitTarih,
    filters.tarihTipi !== 'data_tarihi' ? filters.tarihTipi : null,
  ].filter(Boolean).length;

  const handleTabKeyDown = (e) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
    e.preventDefault();
    const idx = TAB_DEFS.findIndex((t) => t.id === activeTab);
    const dir = e.key === 'ArrowRight' ? 1 : -1;
    const nextId = TAB_DEFS[(idx + dir + TAB_DEFS.length) % TAB_DEFS.length].id;
    setActiveTab(nextId);
    requestAnimationFrame(() => {
      document.getElementById(`${tablistId}-tab-${nextId}`)?.focus();
    });
  };

  // Yanıttaki `danisman_id` doluysa (tekil bir danışmana daraltılmış kapsam
  // — ya danışman rolünün kendisi ya da admin'in seçtiği belirli bir
  // danışman) Atanan Datalar sekmesindeki kırılım tablosu anlamsızdır (bkz.
  // backend yorumu) — tek bir toplam gösterilir.
  const isSingleScope = !!data?.filtreler?.danisman_id;
  const dersActive = !!(data?.filtreler?.ders);

  const metrikler = data?.metrikler || {};
  const m1 = metrikler.satis_adet_data_adet_yuzde || {};
  const m2 = metrikler.tahsilat_satis_yuzde || {};
  const m3 = metrikler.data_basina_arama_adedi || {};

  return (
    <div>
      {/* ── Filtre barı ── */}
      <div className="card-clay p-4 mb-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 mb-3">
          {isAdmin && (
            <div>
              <label htmlFor="ist-danisman" className={LBL_CLS}>{tr('Danışman', 'Advisor')}</label>
              <select
                id="ist-danisman"
                value={filters.danismanId}
                onChange={(e) => setField('danismanId', e.target.value)}
                className={INPUT_CLS}
              >
                <option value="">{tr('Tümü', 'All')}</option>
                {danismanOptionsList(danismanlar, tr).map((o) => (
                  <option key={o.key} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          )}

          {isAdmin && (
            <div>
              <label htmlFor="ist-aktif" className={LBL_CLS}>{tr('Aktif / Pasif', 'Active / Inactive')}</label>
              <select
                id="ist-aktif"
                value={filters.aktif}
                onChange={(e) => setField('aktif', e.target.value)}
                className={INPUT_CLS}
                disabled={!!filters.danismanId}
              >
                {AKTIF_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{lang === 'tr' ? o.tr : o.en}</option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label htmlFor="ist-ders" className={LBL_CLS}>{tr('Eğitim / Ders', 'Course')}</label>
            <input
              id="ist-ders"
              value={filters.ders}
              onChange={(e) => setField('ders', e.target.value)}
              placeholder={tr('Eğitim adı...', 'Course name...')}
              className={INPUT_CLS}
            />
          </div>

          <div className="sm:col-span-2">
            <label className={LBL_CLS}>{tr('Tarihe Göre', 'By Date')}</label>
            <div className="flex gap-3 mb-1 flex-wrap">
              {TARIH_TIPI_OPTIONS.map((o) => (
                <label key={o.value} className="flex items-center gap-1 text-xs font-semibold cursor-pointer">
                  <input
                    type="radio"
                    name="ist-tarih-tipi"
                    value={o.value}
                    checked={filters.tarihTipi === o.value}
                    onChange={() => setField('tarihTipi', o.value)}
                    className="accent-[var(--brand-primary)]"
                  />
                  {lang === 'tr' ? o.tr : o.en}
                </label>
              ))}
            </div>
            <div className="flex gap-1">
              <input
                type="date"
                value={filters.basTarih}
                onChange={(e) => setField('basTarih', e.target.value)}
                className={INPUT_CLS + ' text-xs'}
                aria-label={tr('Başlangıç tarihi', 'Start date')}
              />
              <input
                type="date"
                value={filters.bitTarih}
                onChange={(e) => setField('bitTarih', e.target.value)}
                className={INPUT_CLS + ' text-xs'}
                aria-label={tr('Bitiş tarihi', 'End date')}
              />
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={applyFilters}
            disabled={loading}
            className="flex items-center gap-2 px-5 py-2 rounded-xl bg-emerald-500 text-white text-sm font-bold border-2 border-emerald-600 hover:bg-emerald-600 disabled:opacity-50"
          >
            <Search size={15} />
            {tr('Listele', 'List')}
          </button>
          <button
            onClick={clearFilters}
            className="px-4 py-2 rounded-xl border-2 border-[var(--border-light)] text-sm font-semibold text-[var(--text-muted)] hover:bg-gray-50"
          >
            {tr('Filtreleri Temizle', 'Clear Filters')}
          </button>
          {activeFilterCount > 0 && (
            <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-[var(--brand-primary)] text-white">
              {activeFilterCount} {tr('aktif filtre', 'active filters')}
            </span>
          )}
        </div>
      </div>

      {/* ── İçerik ── */}
      {loading && (
        <div className="space-y-4">
          <KpiSkeleton cols={3} />
          <div className="card-clay p-4 space-y-2">
            {[1, 2, 3, 4, 5].map((i) => <Skeleton key={i} className="h-9 w-full" />)}
          </div>
        </div>
      )}

      {!loading && error && <ErrorRetryCrm onRetry={() => fetchData(filters)} tr={tr} />}

      {!loading && !error && data && (
        <>
          {/* ── 3 metrik kutusu ── */}
          {dersActive && (
            <p
              className="flex items-start gap-2 text-xs font-semibold p-2.5 rounded-lg mb-3"
              style={{ backgroundColor: 'rgba(46,107,143,0.08)', border: '1px solid rgba(46,107,143,0.25)', color: 'var(--crm-info)' }}
            >
              <Info size={13} className="flex-shrink-0 mt-0.5" aria-hidden="true" />
              <span>
                {tr(
                  'Eğitim/ders filtresi yalnızca data (lead) tabanlı sayıları etkiler — aşağıdaki "Satış Adet" ve "Tahsilat" değerleri seçili eğitimden bağımsız, dönemin tamamını kapsar.',
                  'The course filter only affects lead-based counts — the "Sales Count" and "Collections" figures below cover the whole period, independent of the selected course.'
                )}
              </span>
            </p>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
            <KpiCard
              icon={TrendingUp}
              label={tr('Satış Adet / Data Adet', 'Sales / Leads')}
              value={`${m1.satis_adet ?? 0} / ${m1.data_adet ?? 0}`}
              sub={m1.yuzde != null ? fmtPercent(m1.yuzde, lang) : tr('Hesaplanacak veri yok', 'No data yet')}
              subColor={m1.yuzde != null ? 'var(--crm-success)' : 'var(--crm-muted)'}
            />
            <KpiCard
              icon={Wallet}
              label={tr('Tahsilat / Satış', 'Collected / Sold')}
              value={`${fmtMoney(m2.tahsil_edilen)} / ${fmtMoney(m2.satilan_toplam)}`}
              sub={m2.yuzde != null ? fmtPercent(m2.yuzde, lang) : tr('Hesaplanacak veri yok', 'No data yet')}
              subColor={m2.yuzde != null ? 'var(--crm-success)' : 'var(--crm-muted)'}
            />
            <KpiCard
              icon={PhoneCall}
              label={tr('Data Başına Arama Adedi', 'Calls per Lead')}
              value={fmtRatio(m3.ortalama, lang)}
              sub={`${m3.toplam_arama ?? 0} ${tr('arama', 'calls')} / ${m3.data_adet ?? 0} ${tr('data', 'leads')}`}
            />
          </div>

          {/* ── Sekmeler ── */}
          <div
            role="tablist"
            aria-label={tr('İstatistik sekmeleri', 'Statistics tabs')}
            className="flex gap-1 mb-0 border-b-2 overflow-x-auto"
            style={{ borderColor: 'var(--crm-card-border)' }}
          >
            {TAB_DEFS.map((tab) => (
              <button
                key={tab.id}
                id={`${tablistId}-tab-${tab.id}`}
                role="tab"
                aria-selected={activeTab === tab.id}
                aria-controls={`${tablistId}-panel-${tab.id}`}
                tabIndex={activeTab === tab.id ? 0 : -1}
                onClick={() => setActiveTab(tab.id)}
                onKeyDown={handleTabKeyDown}
                className={`px-4 py-2.5 text-sm font-bold rounded-t-xl transition-all border-b-2 -mb-0.5 whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'bg-white'
                    : 'border-transparent text-[var(--text-muted)] hover:text-[var(--text-main)] hover:bg-white/50'
                }`}
                style={activeTab === tab.id ? { borderColor: 'var(--crm-navy)', color: 'var(--crm-navy)' } : undefined}
              >
                {lang === 'tr' ? tab.tr : tab.en}
              </button>
            ))}
          </div>

          <div
            role="tabpanel"
            id={`${tablistId}-panel-${activeTab}`}
            aria-labelledby={`${tablistId}-tab-${activeTab}`}
            className="card-clay overflow-hidden"
          >
            {activeTab === 'durum_dagilimi' && (
              <DurumDagilimiTab rows={data.sekmeler.durum_dagilimi} statusOptions={statusOptions} lang={lang} tr={tr} />
            )}
            {activeTab === 'atanan_datalar' && (
              <AtananDatalarTab
                block={data.sekmeler.atanan_datalar}
                isDanisman={isDanisman}
                isSingleScope={isSingleScope}
                lang={lang}
                tr={tr}
              />
            )}
            {activeTab === 'arama_dagilimi' && (
              <AramaDagilimiTab block={data.sekmeler.arama_dagilimi} lang={lang} tr={tr} />
            )}
            {activeTab === 'ogrenci_satis' && (
              <OgrenciSatisTab block={data.sekmeler.ogrenci_satis} dersActive={dersActive} tr={tr} />
            )}
            {activeTab === 'tahsilat' && (
              <TahsilatTab block={data.sekmeler.tahsilat} dersActive={dersActive} tr={tr} />
            )}
            {activeTab === 'datanin_satis_durumu' && (
              <DataninSatisDurumuTab block={data.sekmeler.datanin_satis_durumu} lang={lang} tr={tr} />
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ─── Ana sayfa ──────────────────────────────────────────────────────────────

export default function AdminDanismanIstatistik() {
  const { lang } = useLanguage();
  const { authHeaders, user } = useAuth();
  const tr = (t, e) => (lang === 'tr' ? t : e);
  const isDanisman = user?.role === 'danisman';
  const isAdmin = !isDanisman;

  return (
    <AdminLayout>
      <div className="-m-4 lg:-m-6 p-4 lg:p-6 min-h-full" style={{ backgroundColor: 'var(--crm-cream)' }}>
        <PageHeader
          breadcrumbs={[rootBreadcrumb(user?.role, tr), { label: tr('Danışman İstatistikleri', 'Consultant Statistics') }]}
          title={tr('Danışman İstatistikleri', 'Consultant Statistics')}
          subtitle={
            isDanisman
              ? tr('Kendi satış, tahsilat ve data performansınızı filtreleyip görüntüleyin.', 'Filter and view your own sales, collections and lead performance.')
              : tr('Danışman bazında satış, tahsilat ve data performansını filtreleyin.', 'Filter sales, collections and lead performance by advisor.')
          }
        />

        <ErrorBoundary fallback={(_err, retry) => <BlockErrorFallback onRetry={retry} tr={tr} />}>
          <IstatistikContent lang={lang} tr={tr} authHeaders={authHeaders} isAdmin={isAdmin} isDanisman={isDanisman} />
        </ErrorBoundary>
      </div>
    </AdminLayout>
  );
}
